import { useState } from 'react';
import { PATHS } from '@client/routes';
import {
  Actions,
  Async,
  F1,
  TMap,
  Typed,
  TypedValue,
} from '../../helpers/types';
import {
  UseCollectionsModelParams,
  useCollectionsModel,
  CollectionsDispatcher,
} from './collections.model';
import {
  CollectionsModel,
  LiteCollection,
  LiteCollectionType,
  ProgramLinksModalData,
} from './collections.domain';
import { Option } from '@client/new/helpers/types';
import _ from 'lodash';
import { SmartTableProps } from '@client/new/components/SmartTable/SmartTable';
import { useTabHash } from '@client/new/hooks/useTabHash';
import { getCurrentPath } from '@client/helpers/routes';
import {
  getEmptyState,
  getOtpLinksModalData,
  getProgramLinks,
  getSubLinks,
  toCollectionsMode,
} from './collections.mappers';
import { useCollectionDuplicationVM } from './collectionDuplication.viewModel';
import auth from '@client/components/auth';
import {
  UploadProgramModel,
  useUploadProgramModel,
} from '@client/new/pages/collections/uploadProgram.model';
import {
  SetupFirstBundleVM,
  useSetupFirstBundleVM,
} from './setupFirstBundle.vm';

export type ListType = LiteCollectionType | 'all';

export type ViewMode =
  | { type: 'empty' }
  | { type: 'drafts-only'; value: SmartTableProps<number> }
  | { type: 'standard'; value: SmartTableProps<number> };

export type CollectionsViewModel = {
  mode: ViewMode;
  listOptions: Array<Option<ListType>>;
  selectedList: ListType;
  dispatch: CollectionViewDispatcher;
  programDuplication: {
    hasError: boolean;
  };
  influencerId: number;
  createLink: string;
  emptyState: {
    otp: {
      isGuideOpen: boolean;
      closeGuide: () => void;
      openGuide: () => void;
      name: string;
      guideLink: string;
    };
    subscription: {
      name: string;
      guideLink: string;
      create: () => void;
    };
  };
  showCreate: boolean;
  selectionModal: {
    open: boolean;
    close: () => void;
  };
  linksModal: Modal<LinksModalData>;
  otpLinksModal: Modal<ProgramLinksModalData>;
  subLinksModal: Modal<Pick<LinksModalData, 'links'>> & {
    showButton: boolean;
  };
  programUploadModal: Modal<
    Pick<UploadProgramModel, 'uploadSummary' | 'step'>
  > & {
    data: {
      uploadSummary: UploadProgramModel['uploadSummary'];
      step: UploadProgramModel['step'];
    };
    onDrop: (file: File) => void;
    onSubmit: () => void;
  };
  archiveModal: {
    isOpen: boolean;
    close: () => void;
    archive: () => void;
  };
  deleteModal: {
    isOpen: boolean;
    close: () => void;
    onDelete: () => void;
  };
  confirmExitModal: {
    isOpen: boolean;
    close: () => void;
    cancel: () => void;
  };
  showExperimentalProgramUpload: boolean;
  publishToBundleModal: {
    isOpen: boolean;
    close: () => void;
    publish: (programId: number) => void;
    setPublishToPlatformBundle: (value: boolean) => void;
    isPlatformBundleSelected: boolean;
    programId: number;
  };
  removeFromBundleModal: {
    isOpen: boolean;
    close: () => void;
    archive: () => void;
    programId: number;
  };
  setupBundleModal: SetupFirstBundleVM['setupBundleModal'];
  isOtpMigratedToBundle: boolean;
  isOffers35Enabled: boolean;
};

type Modal<T> = {
  isOpen: boolean;
  onClose: () => void;
  data?: T;
};

type LinksModalData = {
  programName: string;
  links: Array<{ title: string; url: string; description: string }>;
};

const listTitles: TMap<ListType, string> = {
  all: 'All',
  archived: 'Archived',
  draft: 'Drafts',
  published: 'Published',
};

const optionTypes: ListType[] = ['all', 'draft', 'published', 'archived'];

export const toListOptions = (): CollectionsViewModel['listOptions'] => {
  return optionTypes.map((value) => ({ value, label: listTitles[value] }));
};

type CollectionsType = 'program' | 'category';

export type CollectionsViewActionsMap = {
  selectList: ListType;
  create: { type: CollectionsType };
  edit: number;
  archiveProgram: number;
  deleteProgram: number;
  publishProgram: number;
  reorder: Array<number>;
  openGuide: null;
  closeGuide: null;
  closeSelectionModal: null;
  closeModal: null;
  linksModal: LiteCollection;
  subLinksModal: null;
  archiveModal: number;
  deleteModal: number;
  duplicateProgram: number;
  retryDuplication: null;
  confirmExitModal: null;
  closeConfirmExitModal: null;
  openProgramUploadModal: null;
  uploadProgram: { file: File };
  confirmProgramUpload: null;
  showPublishToBundleModal: number;
  showRemoveFromBundleModal: number;
  createProgram: null;
};

export type CollectionsViewActions = Actions<CollectionsViewActionsMap>;

export type CollectionsViewState = Pick<CollectionsViewModel, 'selectedList'>;

export type CollectionViewDispatcher = F1<CollectionsViewActions>;
export type Model = CollectionsModel & { dispatch: CollectionsDispatcher };

const getDispatcher =
  (p: {
    asyncModel: Async<Model>;
    setTabHash: (v: ListType) => void;
    setOpenModal: (modal: ModalType) => void;
    onDuplicateProgram: F1<number, void>;
    setOpenConfirmationModal: (open: boolean) => void;
    programUploadModel: UploadProgramModel;
    setupBundle: () => void;
  }): CollectionViewDispatcher =>
  (action) => {
    const { asyncModel, setTabHash, setOpenModal, setOpenConfirmationModal } =
      p;
    if (asyncModel.type !== 'fetched') return;
    const model = asyncModel.value;

    switch (action.type) {
      case 'selectList':
        return setTabHash(action.payload);
      case 'archiveProgram': {
        setOpenModal({ type: 'none' });
        return model.dispatch(action);
      }
      case 'publishProgram':
        return model.dispatch(action);
      case 'reorder':
        return model.dispatch(action);
      case 'closeGuide':
      case 'closeSelectionModal':
      case 'closeModal':
        setOpenModal({ type: 'none' });
        return null;
      case 'createProgram':
        if (model.isBundleCreator && !model.hasAnyBundle) {
          p.setupBundle();
          return null;
        }
        setOpenModal({ type: 'openSelectionModal' });
        return null;
      case 'openGuide':
      case 'subLinksModal':
        setOpenModal({ type: 'subLinksModal' });
        return null;
      case 'openProgramUploadModal':
        setOpenModal({ type: 'programUploadModal' });
        return null;
      case 'linksModal':
      case 'archiveModal':
      case 'deleteModal':
        setOpenModal({ type: action.type, value: action.payload as any });
        return null;
      case 'duplicateProgram':
        return p.onDuplicateProgram(action.payload);
      case 'retryDuplication':
        return p.onDuplicateProgram(0);
      case 'deleteProgram':
        return model.dispatch(action);
      case 'closeConfirmExitModal':
        return setOpenConfirmationModal(false);
      case 'confirmExitModal':
        setOpenModal({ type: 'none' });
        return setOpenConfirmationModal(false);
      case 'uploadProgram':
        p.programUploadModel.setFile(action.payload.file);
        p.programUploadModel.upload(true);
        return;
      case 'confirmProgramUpload':
        return p.programUploadModel.upload(false);
      case 'showPublishToBundleModal':
        setOpenModal({
          type: 'showPublishToBundleModal',
          value: action.payload,
        });
        return null;
      case 'showRemoveFromBundleModal':
        setOpenModal({
          type: 'showRemoveFromBundleModal',
          value: action.payload,
        });
        return null;
    }
  };

export type ToTablePropsParams = {
  selectedList: CollectionsViewState['selectedList'];
  model: CollectionsModel;
  dispatch: CollectionViewDispatcher;
  isCreatorLive: boolean;
  isBundleCreator: boolean;
  hasPlatformBundle: boolean;
  isPlatformBundleCreator: boolean;
};

export type ToViewModeParams = Omit<ToTablePropsParams, 'isAdmin'>;

export type ModalType =
  | Typed<'none'>
  | Typed<'openGuide'>
  | Typed<'openSelectionModal'>
  | Typed<'subLinksModal'>
  | Typed<'programUploadModal'>
  | Typed<'setupFirstBundle'>
  | TypedValue<'linksModal', LiteCollection>
  | TypedValue<'archiveModal', number>
  | TypedValue<'deleteModal', number>
  | TypedValue<'showRemoveFromBundleModal', number>
  | TypedValue<'showPublishToBundleModal', number>;

export type ViewState = {
  openModal: ModalType;
  openConfirmationModal: boolean;
};

export const useCollectionsViewModel = (
  p: UseCollectionsModelParams,
): Async<CollectionsViewModel> => {
  const [viewState, setViewState] = useState<ViewState>({
    openModal: { type: 'none' },
    openConfirmationModal: false,
  });
  const { tabHash, setTabHash } = useTabHash(optionTypes, 'all');
  const asyncModel = useCollectionsModel(p);
  const duplicateVM = useCollectionDuplicationVM(asyncModel);
  const programUploadModel = useUploadProgramModel(Number(p.influencerId));
  const creatorId =
    asyncModel.type === 'fetched' ? asyncModel.value.influencerId : 0;
  const { setupBundle, setupBundleModal } = useSetupFirstBundleVM({
    creatorId,
  });

  const setOpenModal = (modal: ModalType) =>
    setViewState((prev) => ({ ...prev, openModal: modal }));

  const setOpenConfirmationModal = (open: boolean) =>
    setViewState((prev) => ({ ...prev, openConfirmationModal: open }));

  if (asyncModel.type !== 'fetched') return asyncModel;
  if (duplicateVM.type !== 'fetched') return duplicateVM;

  const model = asyncModel.value;

  const dispatch = getDispatcher({
    asyncModel,
    setTabHash,
    setOpenModal,
    onDuplicateProgram: duplicateVM.value.lazyDispatch('DuplicateProgram'),
    setOpenConfirmationModal,
    programUploadModel,
    setupBundle,
  });
  const createLink = PATHS.NEW_PROGRAM(model.influencerId, {
    salesType: p.salesType,
    from: getCurrentPath(true),
  });
  const isCreatorLive =
    model.isCreatorLive && model.collections.published.length > 0;

  return {
    type: 'fetched',
    value: {
      listOptions: toListOptions(),
      dispatch,
      selectedList: tabHash,
      mode: toCollectionsMode({
        model,
        dispatch,
        selectedList: tabHash,
        isCreatorLive,
        isBundleCreator: model.isBundleCreator,
        hasPlatformBundle: model.hasPlatformBundle,
        isPlatformBundleCreator: model.isPlatformBundleCreator,
      }),
      programDuplication: {
        hasError: duplicateVM.value.hasDuplicationFailed,
      },
      influencerId: model.influencerId,
      createLink,
      emptyState: getEmptyState(model, dispatch, viewState.openModal),
      showCreate: model.hasAnyCollection,
      isOtpMigratedToBundle: model.isOtpMigratedToBundle,
      isOffers35Enabled: model.isOffers35Enabled,
      selectionModal: {
        open: viewState.openModal.type === 'openSelectionModal',
        close: () => dispatch({ type: 'closeSelectionModal' }),
      },
      linksModal: {
        isOpen: viewState.openModal.type === 'linksModal',
        onClose: () => dispatch({ type: 'closeModal' }),
        data: getProgramLinks(viewState.openModal),
      },
      otpLinksModal: {
        isOpen: viewState.openModal.type === 'linksModal',
        onClose: () => dispatch({ type: 'closeModal' }),
        data: getOtpLinksModalData(
          viewState.openModal,
          model.bundles,
          model.featureCodeById,
          model.isOffers35Enabled,
          model.isOtpMigratedToBundle,
        ),
      },
      subLinksModal: {
        showButton: isCreatorLive,
        isOpen: viewState.openModal.type === 'subLinksModal',
        onClose: () => dispatch({ type: 'closeModal' }),
        data: getSubLinks(model.customURL, model.isBundleCreator),
      },
      programUploadModal: {
        isOpen: viewState.openModal.type === 'programUploadModal',
        onClose: () => {
          programUploadModel.reset();
          dispatch({ type: 'closeModal' });
        },
        onDrop: (file) =>
          dispatch({ type: 'uploadProgram', payload: { file } }),
        onSubmit: () => dispatch({ type: 'confirmProgramUpload' }),
        data: {
          step: programUploadModel.step,
          uploadSummary: programUploadModel.uploadSummary,
        },
      },
      archiveModal: {
        isOpen: viewState.openModal.type === 'archiveModal',
        close: () => dispatch({ type: 'closeModal' }),
        archive: () =>
          dispatch({
            type: 'archiveProgram',
            payload:
              viewState.openModal.type === 'archiveModal'
                ? viewState.openModal.value
                : 0,
          }),
      },
      deleteModal: {
        isOpen: viewState.openModal.type === 'deleteModal',
        close: () => dispatch({ type: 'closeModal' }),
        onDelete: () =>
          dispatch({
            type: 'deleteProgram',
            payload:
              viewState.openModal.type === 'deleteModal'
                ? viewState.openModal.value
                : 0,
          }),
      },
      confirmExitModal: {
        isOpen: viewState.openConfirmationModal,
        close: () => dispatch({ type: 'closeConfirmExitModal' }),
        cancel: () => dispatch({ type: 'confirmExitModal' }),
      },
      showExperimentalProgramUpload: (
        import.meta.env.VITE_EXPERIMENTAL_FEATURES_USERS || ''
      )
        .split(',')
        .includes(auth.getUser().email),
      publishToBundleModal: {
        isOpen: viewState.openModal.type === 'showPublishToBundleModal',
        close: () => dispatch({ type: 'closeModal' }),
        programId:
          viewState.openModal.type === 'showPublishToBundleModal'
            ? viewState.openModal.value
            : 0,
        publish: (value) => {
          model.dispatch({ type: 'publishProgram', payload: value });
          dispatch({ type: 'closeModal' });
        },
        setPublishToPlatformBundle: (value: boolean) => {
          model.dispatch({
            type: 'setPublishToPlatformBundle',
            payload: value,
          });
        },
        isPlatformBundleSelected: model.publishToPlatformBundle,
      },
      removeFromBundleModal: {
        isOpen: viewState.openModal.type === 'showRemoveFromBundleModal',
        close: () => dispatch({ type: 'closeModal' }),
        programId:
          viewState.openModal.type === 'showRemoveFromBundleModal'
            ? viewState.openModal.value
            : 0,
        archive: () =>
          dispatch({
            type: 'archiveProgram',
            payload:
              viewState.openModal.type === 'showRemoveFromBundleModal'
                ? viewState.openModal.value
                : 0,
          }),
      },
      setupBundleModal,
    },
  };
};
